import OrdersService from '@/api/services/orders.service';
import PosV2CoversModal, {
  IPosV2CoversResult,
} from '@/components/pos-v2/modals/PosV2CoversModal.vue';
import { useUi } from '@/composition/use-ui';
import { getCoversAcceptPromptTarget } from '@/domain/pos/covers-accept';
import { OrderType } from '@/enums/OrderEnums';
import { IOrder } from '@/interfaces/IOrder';
import { showDynamicDialogOn } from '@/lib/modals';
import { i18n } from '@/plugins/i18n';
import { useOrdersStore } from '@/stores/orders.store';
import { useRestaurantStore } from '@/stores/restaurant.store';

/**
 * Post-accept covers confirmation, shared by every accept surface (POS V2
 * incoming pane/preview/order view and the classic realtime page / order
 * modal): when the accepted Seat order opened its table and the session has
 * no confirmed covers, ask staff to confirm the party size — prefilled with
 * the customer's suggestion.
 *
 * Fire-and-forget by design (never throws): the accept already succeeded, a
 * dismissed/skipped prompt stores nothing, and only a failed save toasts.
 *
 * @param modalInstance the caller's vue-js-modal instance (`this.$modal` /
 *   `proxy.$modal`) — plain functions can't reach it through useModal().
 */
export async function maybePromptCoversAfterAccept(
  modalInstance,
  acceptedOrder: IOrder
): Promise<void> {
  try {
    const restaurantStore = useRestaurantStore();
    const ordersStore = useOrdersStore();

    const coversPromptMode = restaurantStore.selectedBranch?.coversPromptMode;

    // Cheap pre-checks before any refetch.
    if ((coversPromptMode ?? 'optional') === 'off') return;
    if (acceptedOrder.type !== OrderType.Seat || acceptedOrder.tableId == null)
      return;

    // Payloads from some sources (e.g. WS new-order events) may lack the
    // read-bridge session embed: refetch once — this also picks up covers
    // confirmed on another device between arrival and accept.
    let order = acceptedOrder;
    if (order.tableSession === undefined) {
      order = await OrdersService.v2GetOrderById(order.restaurantId, order.id);
    }

    const target = getCoversAcceptPromptTarget({
      order,
      liveOrders: ordersStore.orders,
      coversPromptMode,
    });
    if (!target) return;

    const tableName =
      restaurantStore.tables?.find((t) => t.id === order.tableId)?.name ??
      order.table?.name ??
      null;

    // Unique dialog name: rapid-fire accepts on different tables must not
    // collide on vue-js-modal's name-keyed hide().
    const result = await showDynamicDialogOn<IPosV2CoversResult | null>(
      modalInstance,
      PosV2CoversModal,
      {
        tableName,
        suggested: target.suggested,
      },
      `pos-covers-accept-${order.id}`,
      { width: 420 }
    );

    // Dismissed or skipped: store nothing — the session already has
    // covers = null, and the table-header chip keeps nudging.
    if (result?.covers == null) return;

    try {
      const updated = await OrdersService.patchTableSessionCovers(
        target.sessionId,
        result.covers
      );

      // Optimistic mirror on the session embeds (same reactive objects as the
      // table page) — the server-side notifyOrderUpdate refresh confirms it.
      const mirrorOrders = [
        acceptedOrder,
        order,
        ...(ordersStore.orders ?? []),
      ];
      for (const mirrorOrder of mirrorOrders) {
        if (mirrorOrder.tableSession?.id === target.sessionId) {
          mirrorOrder.tableSession.covers = updated.covers;
          if (updated.updatedAt) {
            mirrorOrder.tableSession.updatedAt = updated.updatedAt;
          }
        }
      }
    } catch (error) {
      useUi().serverError(error, String(i18n.t('posV2.coversSaveError')));
    }
  } catch (error) {
    console.error('Error prompting covers after accept', error);
  }
}
